import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { Menu, X, Globe } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export const Navbar = () => {
  const { language, toggleLanguage, t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  const navLinks = [
    { id: 'showcase', label: t('nav_features') },
    { id: 'pricing', label: t('nav_pricing') },
    { id: 'advantages', label: t('nav_advantages') },
    { id: 'logitrak', label: t('nav_logitrak') },
    { id: 'contact', label: t('nav_contact') },
  ];

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white/80 backdrop-blur-md shadow-sm border-b border-slate-100' 
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <a 
            href="https://logirent.ch/" 
            className="flex items-center gap-2"
            data-testid="logo-link"
          >
            <div className="w-8 h-8 bg-slate-900 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">L</span>
            </div>
            <span className="font-semibold text-slate-900 text-lg tracking-tight">Logirent</span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => scrollToSection(link.id)}
                className="text-slate-600 hover:text-slate-900 transition-colors text-sm font-medium"
                data-testid={`nav-${link.id}`}
              >
                {link.label}
              </button>
            ))}
            <a
              href={language === 'fr' ? '/blog/' : language === 'en' ? '/en/blog/' : '/blog/'}
              className="text-slate-600 hover:text-slate-900 transition-colors text-sm font-medium"
              data-testid="nav-blog"
            >
              Blog
            </a>
            <a
              href="/a-propos/"
              className="text-slate-600 hover:text-slate-900 transition-colors text-sm font-medium"
              data-testid="nav-about"
            >
              {language === 'fr' ? 'À propos' : 'About'}
            </a>
          </div>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center gap-3">
            <button
              onClick={() => { if (language === 'fr') { toggleLanguage(); } else { window.location.href = '/de/'; } }}
              className="flex items-center gap-1.5 text-slate-600 hover:text-slate-900 transition-colors text-sm font-medium px-2 py-1.5 rounded-md hover:bg-slate-50"
              data-testid="language-toggle"
              title={language === 'fr' ? 'Switch to English' : 'Zur deutschen Version'}
            >
              <Globe className="w-4 h-4" />
              <span>{language.toUpperCase()}</span>
            </button>
            <Button 
              asChild
              variant="outline"
              className="border-slate-200 text-slate-700 hover:bg-slate-50 rounded-md px-4 h-9"
              data-testid="nav-login"
            >
              <a href="https://app.logirent.ch/" target="_blank" rel="noopener noreferrer">
                {t('nav_login')}
              </a>
            </Button>
            <Button 
              onClick={() => scrollToSection('contact')}
              className="bg-slate-900 hover:bg-slate-800 text-white rounded-md px-4 h-9"
              data-testid="nav-cta"
            >
              {t('hero_cta_primary')}
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <div className="flex md:hidden items-center gap-3">
            <button
              onClick={() => { if (language === 'fr') { toggleLanguage(); } else { window.location.href = '/de/'; } }}
              className="text-slate-600 hover:text-slate-900 p-2"
              data-testid="mobile-language-toggle"
            >
              <Globe className="w-5 h-5" />
            </button>
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="text-slate-900 p-2"
              data-testid="mobile-menu-toggle"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white border-b border-slate-100"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-4 space-y-4">
              {navLinks.map((link) => (
                <button
                  key={link.id}
                  onClick={() => scrollToSection(link.id)}
                  className="block w-full text-left text-slate-600 hover:text-slate-900 py-2 text-base font-medium"
                  data-testid={`mobile-nav-${link.id}`}
                >
                  {link.label}
                </button>
              ))}
              <a
                href={language === 'fr' ? '/blog/' : language === 'en' ? '/en/blog/' : '/blog/'}
                className="block w-full text-left text-slate-600 hover:text-slate-900 py-2 text-base font-medium"
                data-testid="mobile-nav-blog"
              >
                Blog
              </a>
              <a
                href="/a-propos/"
                className="block w-full text-left text-slate-600 hover:text-slate-900 py-2 text-base font-medium"
                data-testid="mobile-nav-about"
              >
                {language === 'fr' ? 'À propos' : 'About'}
              </a>
              <Button 
                onClick={() => { scrollToSection('contact'); setIsMobileMenuOpen(false); }}
                className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-md"
                data-testid="mobile-nav-cta"
              >
                {t('hero_cta_primary')}
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};
